import { useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import useClickAway from "@/core/helpers/useClickAway";
import { Button, Icon } from "@/shared/ui";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";

type Props = {
  title: string;
  onClose: () => void;
  onConfirm: (text: string) => void;
  isLoading?: boolean;
  isConfirming: boolean;
  faction: FactionEnum;
};

export default function FactionInputModal({
  title,
  onClose,
  onConfirm,
  isLoading,
  isConfirming,
  faction,
}: Props) {
  const { t } = useTranslation("faction");

  const [inputValue, setInputValue] = useState("");
  const ref = useClickAway(onClose);

  let gradient = "oliveShadow";
  if (faction === FactionEnum.HOSPITAL) {
    gradient = "redShadow";
  }
  if (faction === FactionEnum.MEDSI || faction === FactionEnum.REGION24) {
    gradient = "lightBlueShadow";
  }

  return (
    <div
      className={clsx(
        "absolute z-10 top-0 left-0 right-0 bottom-0 bg-[#0F0F0F]/95",
        "flex items-center justify-center",
        "animate-[--animation-fade-in]",
      )}
    >
      {isLoading && (
        <div className="flex items-center justify-center p-4">
          <div className="mz-loader" />
        </div>
      )}

      {!isLoading && (
        <div
          ref={ref}
          className={cn(
            "w-[25.125rem] p-5 bg-gradient-to-b from-[#0F0F0F] to-[#0F0F0F]/90 rounded-[0.875rem]",
            "relative p-7.5 flex flex-col items-center",
            gradient,
          )}
        >
          <Button
            variant="surface"
            color="gray"
            className="absolute top-2.5 right-2.5 size-7.5 p-0"
            onClick={onClose}
          >
            <Icon name="close" className="w-3" />
          </Button>
          <div className="text-18 font-bold text-white">{title}</div>
          <div className="h-px w-[20rem] bg-[#1F1F1F]/80 flex justify-center my-5">
            <div className="h-full w-[5rem] bg-accent-solid" />
          </div>
          <textarea
            value={inputValue}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInputValue(e.target.value)}
            maxLength={70}
            placeholder={t("faction.inputReason")}
            spellCheck={false}
            className="w-full min-h-15 mb-4 px-4 py-4.5 text-14 font-medium placeholder:text-white/50 bg-dark-800 rounded-xl resize-none [--scrollbar-track-bg:#181818] [--scrollbar-thumb-bg:#434242] [scrollbar-gutter:stable]"
          />
          <div className="grid grid-cols-2 gap-2 w-full">
            <Button
              className="gap-2.5 h-11"
              onClick={() => onConfirm(inputValue)}
              loading={isConfirming}
              disabled={isConfirming || !inputValue}
            >
              {t("faction.yes")}
            </Button>
            <Button
              variant="solid-surface"
              className="h-11"
              onClick={onClose}
              loading={isConfirming}
              disabled={isConfirming}
            >
              {t("faction.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
